import { roleApi } from '@/api/account-management/roleApi';
import { createRole, type Permission, type Role } from '@/models/account-management/Role';
import AddIcon from '@mui/icons-material/Add';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import { DataGrid, type GridColDef, type GridRowParams } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Controller, useForm, type SubmitHandler } from 'react-hook-form';
import { useAppContext } from '../../AppContext';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { usePermissions } from '../../permissions/usePermissions';
import { Perm } from '../../permissions/permissions';
import { fillHeight } from "../../theme";
import DataGridDeleteButton from '../common/data-grid/DataGridDeleteButton';
import DataGridEditButton from '../common/data-grid/DataGridEditButton';
import Dialog from '../common/dialog/Dialog';
import Spacer from '../common/Spacer';
import { useToast } from '../common/toast/ToastProvider';
import Permissions from './Permissions';
import UserTransferList from './UserTransferList';
import TabWithError from '../common/TabWithError';

// How many affected users to name in the delete warning before summarising the rest.
const DELETE_WARNING_USER_LIMIT = 10;

// The alert renders its message as HTML, so anything interpolated from data has to be escaped —
// a user's name is entered by hand and would otherwise be markup.
const escapeHtml = (value: string) =>
    value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

const RoleList = () => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogTitleName, setDialogTitleName] = useState<string | undefined>();
    const [tabValue, setTabValue] = useState<string>('detail');
    const { control, reset, register, handleSubmit: onFormSubmit, formState } = useForm<Role>();
    const qc = useQueryClient();
    const app = useAppContext();
    // Roles follow the viewed org (super-admins can manage any org); the query re-runs when the org changes.
    const { data: roleList, isPending: isGridLoading } = useQuery<Role[]>({ queryKey: ['roles', app.organizationId], queryFn: () => roleApi.list(app.organizationId), staleTime: Infinity });
    const { data: permissionList } = useQuery<Permission[]>({ queryKey: ['permissions'], queryFn: roleApi.listPermissions, staleTime: Infinity });
    const { mutateAsync: saveRole, isPending: isSaving } = useMutation({ mutationFn: (r: Role) => roleApi.save(r, app.organizationId), onSuccess: () => handleSaveSuccess(), onError: () => handleSaveError() });
    const { mutateAsync: deleteRole } = useMutation({ mutationFn: roleApi.delete, onSuccess: () => handleDeleteSuccess(), onError: () => handleDeleteError() }); //react-query to delete the role
    const toast = useToast();
    const strings = useLocalizedStrings();
    const perms = usePermissions();
    // Roles are managed under the Users page and share the `users` permission.
    const canCreate = perms.canCreate(Perm.users);
    const canUpdate = perms.canUpdate(Perm.users);
    const canDelete = perms.canDelete(Perm.users);

    // Creating and editing are different permissions; dialogTitleName is only set when editing an existing role.
    const dialogReadOnly = dialogTitleName ? !canUpdate : !canCreate;

    const columns: GridColDef<Role>[] = [
        {
            field: 'name',
            headerName: strings.name,
            flex: 2,
        },
        {
            field: 'enabled',
            headerName: strings.enabled,
            type: 'boolean',
            flex: 1
        },
        {
            field: 'actions',
            headerName: strings.actions,
            type: 'actions',
            flex: 1,
            getActions: (params: GridRowParams) => [
                <DataGridEditButton id={params.id.toString()} onClick={handleEditClick} aria-label="edit role" readOnly={!canUpdate} />,
                <DataGridDeleteButton id={params.id.toString()} getWarningMessage={getDeleteWarningMessage} onClick={handleDeleteClick} aria-label="delete role" hidden={!canDelete} />
            ],
        },
    ];

    useEffect(() => {
        if (!dialogOpen) {
            setDialogTitleName(undefined);
            reset(undefined);
            setTabValue('detail');
        }
    }, [dialogOpen, reset]);

    const handleAddClick = () => {
        reset(createRole(permissionList ?? []));
        setDialogOpen(true);
    };

    const handleEditClick = (id: string) => {
        const role = roleList?.find(u => u.id === id);
        if (!role) return;
        reset(role);
        setDialogTitleName(role.name);
        setDialogOpen(true);
    };

    const getDeleteWarningMessage = (id: string) => {
        const role = roleList?.find(o => o.id === id);
        const message = strings.roleConfirmDeleteMessgage.replace("{{name}}", escapeHtml(role?.name ?? ""));

        // Deleting a role clears it from everyone holding it, so say who that is before they agree.
        const users = role?.users ?? [];
        if (users.length === 0) return message;

        const names = users
            .map(u => `${u.lastName}, ${u.firstName}`)
            .sort((a, b) => a.localeCompare(b));
        // A role can have far more members than fits in a dialog; show enough to recognise the group.
        const shown = names.slice(0, DELETE_WARNING_USER_LIMIT);
        const remaining = names.length - shown.length;

        return message
            + strings.roleConfirmDeleteUsers
                .replace("{{count}}", String(names.length))
                .replace("{{users}}", escapeHtml(shown.join("; ")))
            + (remaining > 0 ? strings.roleConfirmDeleteUsersMore.replace("{{count}}", String(remaining)) : "");
    }

    const handleDeleteClick = async (id: string) => {
        await deleteRole(id);
    };

    const handleDeleteSuccess = async () => {
        toast({ severity: "success", message: strings.roleDeleteSuccess });
        await qc.invalidateQueries({ queryKey: ['roles'] });
        // Deleting a role clears it from every user that held it.
        await qc.invalidateQueries({ queryKey: ['users'] });
    }

    const handleDeleteError = () => {
        toast({ severity: "error", message: strings.roleDeleteError });
    }

    const handleSubmit: SubmitHandler<Role> = async (data) => {
        console.log(data);
        await saveRole(data); // Run the save mutation with the current form data
    }

    const handleSaveSuccess = async () => {
        toast({ severity: "success", message: strings.roleSaveSuccess });
        await qc.invalidateQueries({ queryKey: ['roles'] });
        // Role membership changed here, so the Users tab must refetch.
        await qc.invalidateQueries({ queryKey: ['users'] });
        setDialogOpen(false);
    }

    const handleSaveError = () => {
        toast({ severity: "error", message: strings.roleSaveError });
    }

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if ((event.ctrlKey || event.metaKey) && event.key === 'a') {
            event.preventDefault();

            // Set all permissions to have full access (15) for the current role in the form state
            qc.setQueryData(['role'], (role: Role | undefined) =>
                role
                    ? {
                        ...role,
                        permissions: role.permissions.map(p => ({ ...p, access: 15 }))
                    }
                    : role
            );
        }
    };

    const tabErrors = {
        detail: !!(
            formState.errors.name            
        )
    };

    return (
        <>
            <Box sx={{ ...fillHeight, overflow: 'hidden', p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {canCreate && <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddClick}>{strings.roleNewTitle}</Button>}
                </Box>
                <Spacer y={2} />
                <Box sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
                    <DataGrid
                        rows={roleList}
                        columns={columns}
                        autoPageSize={true}
                        loading={isGridLoading}
                        localeText={{ noRowsLabel: strings.rolesNotFound }}
                    />
                </Box>
            </Box>

            <form onSubmit={onFormSubmit(handleSubmit)} id="dialog-role-form">
                {/* TabContext wraps the Dialog rather than sitting inside it, because the tab bar is passed
                    through the `tabs` slot and has to resolve the same context as the panels below. */}
                <TabContext value={tabValue}>
                <Dialog
                    open={dialogOpen}
                    title={dialogTitleName ? strings.roleEditTitle.replace("{{name}}", dialogTitleName) : strings.roleNewTitle}
                    formId="dialog-role-form"
                    formIsDirty={formState.isDirty}
                    saving={isSaving}
                    saveButtonTitle={strings.save}
                    onCancel={() => setDialogOpen(false)}
                    cancelButtonTitle={strings.cancel}
                    pad={false}
                    onKeyDown={handleKeyDown}
                    readOnly={dialogReadOnly}
                    tabs={
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: (theme) => theme.palette.background.paper }}>
                            <TabList onChange={(_, newValue) => setTabValue(newValue)} aria-label="tabs">
                                <TabWithError label={strings.details} value="detail" hasError={tabErrors.detail} />
                                <Tab label={strings.permissions} value="permissions" />
                                <Tab label={strings.users} value="users" />
                            </TabList>
                        </Box>
                    }
                >
                        {/* <Box sx={!app.isSuperAdmin() ? { width: 736, height: 416, overflow: 'hidden' } : {}}> Set fixed size when tabs are shown to prevent dialog resizing  */}
                        <Box sx={{ width: 736, height: 416, overflow: 'hidden' }}>
                            <TabPanel id="detail" value="detail" keepMounted>
                                <TextField
                                    id="name"
                                    label={strings.name}
                                    variant="outlined"
                                    sx={{ width: 400 }}
                                    {...register("name", {
                                        required: strings.validationNameRequired
                                    })}
                                    error={!!formState.errors.name}
                                    helperText={formState.errors.name?.message || " "}
                                />
                                <Spacer />
                                <FormControl size="input">
                                    <FormControlLabel
                                        label={strings.enabled}
                                        control={
                                            <Controller
                                                name="enabled"
                                                control={control}
                                                render={({ field }) => (
                                                    <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                                                )}
                                            />
                                        }
                                    />
                                </FormControl>
                            </TabPanel>
                            <TabPanel id="permissions" value="permissions" keepMounted sx={{ pt: 0, px: 0 }} >
                                <Controller
                                    name="permissions"
                                    control={control}
                                    render={({ field }) => (
                                        <Permissions permissions={field.value} onChange={field.onChange} />
                                    )}
                                />
                            </TabPanel>
                            <TabPanel id="users" value="users" keepMounted>
                                <Controller
                                    name="users"
                                    control={control}
                                    render={({ field }) => (
                                        <UserTransferList
                                            selected={field.value}
                                            setSelected={field.onChange}
                                            listWidth={300}
                                            listHeight={230}
                                        />
                                    )}
                                />
                            </TabPanel>
                        </Box>
                </Dialog>
                </TabContext>
            </form>
        </>
    );
};

export default RoleList;